import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { stockReturnApi as returnApi, warehouseApi } from '../services/api';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
  ClipboardList, Clock, FileText, User, Truck, Calendar,
  Archive, CheckCircle2, AlertTriangle, ArrowRight, Check, X,
  ThumbsDown, ShieldAlert, FileCheck, Info, Plus,
  Search, ChevronDown, ChevronLeft, ChevronRight, FileDown, ArrowLeft, Loader2
} from 'lucide-react';

const { RangePicker } = DatePicker;

function StockReturns() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF' || currentTheme?.color === '#0f172a' || ['Purple', 'Black'].includes(currentTheme?.name);
  const navigate = useNavigate();

  // Navigation State to toggle between the Returns List and the Receive Return Form
  const [showReceiveForm, setShowReceiveForm] = useState(false);

  // Success Toast Popup State (shared)
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success', 'danger', 'warning'

  // Dashboard Filters States
  const [searchQuery, setSearchQuery] = useState('');
  const [courierFilter, setCourierFilter] = useState('All');
  const [warehouseFilter, setWarehouseFilter] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination for Dashboard list (server-side)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // ── WAREHOUSES (for restock dropdown + filter) ──
  const [warehousesList, setWarehousesList] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const w = await warehouseApi.getActiveWarehouses();
        setWarehousesList(Array.isArray(w) ? w : (w?.content || w?.data || []));
      } catch (err) {
        console.error('Warehouse load error:', err);
      }
    })();
  }, []);

  // ── PENDING RETURN QUEUE (API) ──
  const [pendingReturns, setPendingReturns] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);

  const mapPendingReturn = (r) => ({
    id: r.id,
    displayId: r.returnNumber || r.referenceId || `RET-${r.id}`,
    customer: r.customerName || '',
    orderId: r.orderNumber || '',
    courier: r.courierName || '',
    awb: r.awbNumber || '',
    date: r.initiatedDate || '',
    reason: r.reasonCode || r.reason || '',
    products: (r.items || []).map(it => ({
      id: it.id ?? it.returnItemId,
      name: it.productName || '',
      sku: it.sku || '',
      batchNo: it.batchNumber || it.batchNo || '',
      expiryDate: it.expiryDate || '',
      returnedQty: it.returnedQty ?? 0,
      acceptQty: it.returnedQty ?? 0,
      rejectQty: 0,
      condition: 'Good Resalable',
    })),
  });

  const loadPendingQueue = async () => {
    setPendingLoading(true);
    try {
      const res = await returnApi.getPendingQueue();
      const list = res?.content || res?.data || res || [];
      setPendingReturns((Array.isArray(list) ? list : []).map(mapPendingReturn));
    } catch (err) {
      console.error('Pending queue load error:', err);
      setPendingReturns([]);
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    loadPendingQueue();
  }, []);

  // Selected Return Request state
  const [selectedReturnId, setSelectedReturnId] = useState(null);
  const [restockWarehouse, setRestockWarehouse] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);

  const activeReturn = pendingReturns.find(r => r.id === selectedReturnId);

  useEffect(() => {
    if (warehousesList.length > 0 && !restockWarehouse) {
      setRestockWarehouse(String(warehousesList[0].id));
    }
  }, [warehousesList, restockWarehouse]);

  const warehouseName = (id) => warehousesList.find(w => String(w.id) === String(id))?.name || '';

  // Update accept quantity with auto balancing logic (local, pre-submit)
  const handleAcceptQtyChange = (id, val) => {
    setPendingReturns(pendingReturns.map(ret => {
      if (ret.id === selectedReturnId) {
        return {
          ...ret,
          products: ret.products.map(p => {
            if (p.id === id) {
              const cleanVal = val === '' ? 0 : Math.min(p.returnedQty, Math.max(0, parseInt(val) || 0));
              return { ...p, acceptQty: cleanVal, rejectQty: p.returnedQty - cleanVal };
            }
            return p;
          })
        };
      }
      return ret;
    }));
  };

  const handleRejectQtyChange = (id, val) => {
    setPendingReturns(pendingReturns.map(ret => {
      if (ret.id === selectedReturnId) {
        return {
          ...ret,
          products: ret.products.map(p => {
            if (p.id === id) {
              const cleanVal = val === '' ? 0 : Math.min(p.returnedQty, Math.max(0, parseInt(val) || 0));
              return { ...p, rejectQty: cleanVal, acceptQty: p.returnedQty - cleanVal };
            }
            return p;
          })
        };
      }
      return ret;
    }));
  };

  const totalReturnedUnits = activeReturn ? activeReturn.products.reduce((sum, p) => sum + p.returnedQty, 0) : 0;
  const totalAcceptedUnits = activeReturn ? activeReturn.products.reduce((sum, p) => sum + p.acceptQty, 0) : 0;
  const totalRejectedUnits = activeReturn ? activeReturn.products.reduce((sum, p) => sum + p.rejectQty, 0) : 0;

  const conditionToCode = (label) => ({
    'Good Resalable': 'GOOD_RESALABLE',
    'Damaged/Nonsalable': 'DAMAGED_NONSALABLE',
    'Needs Re-packaging': 'NEEDS_REPACKAGING',
    'Expired': 'EXPIRED',
  }[label] || 'GOOD_RESALABLE');

  // ── Handle Actions click (API) ──
  const triggerAction = async (type) => {
    if (!selectedReturnId || !activeReturn) {
      alert('Please select a return from the queue first.');
      return;
    }

    const action = type === 'accept' ? 'RESTOCK' : type === 'reject' ? 'REJECT' : 'HOLD';
    setSubmittingAction(true);
    try {
      await returnApi.inspectReturn(selectedReturnId, {
        items: activeReturn.products.map(p => ({
          returnItemId: p.id,
          acceptedQty: p.acceptQty,
          rejectedQty: p.rejectQty,
          condition: conditionToCode(p.condition),
        })),
        action,
        restockWarehouseId: action === 'RESTOCK' ? Number(restockWarehouse) : undefined,
        inspectionRemarks: remarks || undefined,
        inspectedByName: 'Warehouse QC',
      });

      const msg = action === 'RESTOCK'
        ? `Return request ${activeReturn.displayId} accepted and restocked into ${warehouseName(restockWarehouse)}!`
        : action === 'REJECT'
          ? `Return request ${activeReturn.displayId} rejected and flagged for disposal.`
          : `Return request ${activeReturn.displayId} placed on hold.`;
      setToastType(action === 'RESTOCK' ? 'success' : action === 'REJECT' ? 'danger' : 'warning');
      setToastMessage(msg);
      setShowToast(true);

      setShowReceiveForm(false);
      setSelectedReturnId(null);
      loadPendingQueue();
      loadReturns();

      setTimeout(() => setShowToast(false), 4000);
    } catch (err) {
      alert(err.message || 'Failed to submit inspection');
    } finally {
      setSubmittingAction(false);
    }
  };

  // ── DASHBOARD RETURNS LIST (API, server-side search + pagination) ──
  const [dashboardReturns, setDashboardReturns] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const mapDashboardReturn = (r) => ({
    id: r.id,
    displayId: r.returnNumber || r.referenceId || `RET-${r.id}`,
    date: r.initiatedDate || '',
    orderId: r.orderNumber || '',
    courier: r.courierName || '',
    customer: r.customerName || '',
    itemsCount: r.itemsCount ?? (r.items ? r.items.length : 0),
    status: r.status === 'PENDING' ? 'Pending'
      : r.status === 'RESTOCKED' ? 'Restocked'
      : r.status === 'REJECTED' ? 'Rejected'
      : r.status === 'HOLD' ? 'Inspected'
      : (r.status || 'Pending'),
    reason: r.inspectionRemarks || r.reasonCode || '--',
    warehouse: r.restockWarehouseName || r.warehouse || '',
  });

  const loadReturns = async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await returnApi.searchReturns({
        search: debouncedSearch,
        startDate,
        endDate,
        page: currentPage - 1,
        size: itemsPerPage,
      });
      const list = res?.content || res?.data || res || [];
      setDashboardReturns((Array.isArray(list) ? list : []).map(mapDashboardReturn));
      setTotalCount(res?.totalElements ?? (Array.isArray(list) ? list.length : 0));
    } catch (err) {
      setListError(err.message || 'Failed to load returns');
      console.error('Returns load error:', err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadReturns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, startDate, endDate, currentPage]);

  // Client-side courier/warehouse filters on top of server search
  const filteredReturns = dashboardReturns.filter(ret => {
    const matchesCourier = courierFilter === 'All' || (ret.courier || '').toLowerCase() === courierFilter.toLowerCase();
    const matchesWarehouse = warehouseFilter === 'All' || (ret.warehouse || '').toLowerCase() === warehouseFilter.toLowerCase();
    return matchesCourier && matchesWarehouse;
  });

  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Pending':
        return isDark ? 'bg-amber-950/60 text-amber-300 border border-amber-800/60' : 'bg-amber-50 text-amber-700 border border-amber-200/60';
      case 'Rejected':
        return isDark ? 'bg-rose-950/60 text-rose-300 border border-rose-800/60' : 'bg-rose-50 text-rose-600 border border-rose-200';
      case 'Restocked':
        return isDark ? 'bg-emerald-950/60 text-emerald-300 border border-emerald-800/60' : 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'Inspected':
      default:
        return isDark ? 'bg-slate-800 text-slate-200 border border-slate-700' : 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  const getReasonTextClass = (reason) => {
    if (reason === 'DAMAGED_NONSALABLE' || reason === 'Damaged/Nonsalable') return 'text-rose-500 font-bold';
    if (reason === 'GOOD_RESALABLE' || reason === 'Good Resalable') return 'text-emerald-500 font-bold';
    if (reason === '--') return isDark ? 'text-slate-500 font-medium' : 'text-gray-400 font-medium';
    return isDark ? 'text-slate-300 font-semibold' : 'text-slate-700 font-semibold';
  };

  const kpiPendingCount = pendingReturns.length;
  const kpiCompletedToday = dashboardReturns.filter(r => r.status !== 'Pending').length;
  const totalItemsProcessed = dashboardReturns.reduce((sum, r) => sum + (r.itemsCount || 0), 0);

  // ── Open a specific return in the receive/inspect form ──
  const openReturnInForm = async (returnRow) => {
    const existsInPending = pendingReturns.some(pr => pr.id === returnRow.id);
    if (existsInPending) {
      setSelectedReturnId(returnRow.id);
      setShowReceiveForm(true);
      return;
    }
    try {
      const detail = await returnApi.getReturn(returnRow.id);
      setPendingReturns(prev => [...prev, mapPendingReturn(detail)]);
      setSelectedReturnId(detail.id);
      setShowReceiveForm(true);
    } catch (err) {
      alert(err.message || 'Failed to load return details');
    }
  };

  return (
    <div className={`space-y-6 pb-28 relative p-4 md:p-6 rounded-2xl transition-colors duration-300 ${isDark ? 'bg-[#0f172a] text-white' : 'bg-slate-50/50 text-gray-900'}`}>
      {/* ── TOAST POPUP (SHARED) ── */}
      {showToast && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-[10000] transition-all duration-300">
          <div className={`flex items-center gap-3 border px-4 py-3 rounded-xl shadow-lg ${
            toastType === 'success'
              ? (isDark ? 'bg-emerald-950 border-emerald-800 text-emerald-100' : 'bg-[#eefdf5] border-emerald-500/30 text-gray-800')
              : toastType === 'danger'
                ? (isDark ? 'bg-rose-950 border-rose-800 text-rose-100' : 'bg-[#fdf2f2] border-rose-500/30 text-gray-800')
                : (isDark ? 'bg-amber-950 border-amber-800 text-amber-100' : 'bg-[#fffbeb] border-amber-500/30 text-gray-800')
          }`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white ${
              toastType === 'success' ? 'bg-emerald-500' : toastType === 'danger' ? 'bg-rose-500' : 'bg-amber-500'
            }`}>
              {toastType === 'success' && <Check className="w-3.5 h-3.5 stroke-[3]" />}
              {toastType === 'danger' && <X className="w-3.5 h-3.5 stroke-[3]" />}
              {toastType === 'warning' && <AlertTriangle className="w-3.5 h-3.5 stroke-[3]" />}
            </div>
            <span className="text-sm font-bold">{toastMessage}</span>
            <button
              onClick={() => setShowToast(false)}
              className="w-5 h-5 rounded-full border border-rose-200 text-rose-500 flex items-center justify-center hover:bg-rose-50 transition-all ml-4 cursor-pointer"
            >
              <X className="w-3.5 h-3.5 stroke-[3]" />
            </button>
          </div>
        </div>
      )}

      {/* ── CONDITIONAL RENDER: RECEIVE RETURN FORM VS DASHBOARD LIST ── */}
      {showReceiveForm ? (
        <div className="space-y-6">
          {/* ── HEADER ── */}
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setShowReceiveForm(false); setSelectedReturnId(null); }}
                className={`p-2.5 rounded-xl border hover:opacity-80 transition-all cursor-pointer ${
                  isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700'
                }`}
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h1 className={`text-2xl desktop:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Receive Return
                </h1>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'} mt-1`}>
                  Inspect courier returns — Accept or reject before restocking
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/stock-management/stock-in')}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-all shadow-md cursor-pointer"
                style={{ backgroundColor: currentTheme.color }}
              >
                <Plus className="w-3.5 h-3.5" />
                New Stock In
              </button>
            </div>
          </div>

          {/* ── CARD 1: PENDING RETURN QUEUE ── */}
          <div className={`border rounded-2xl p-6 transition-all ${
            isDark ? 'bg-[#1e293b] border-white/10 shadow-2xl' : 'bg-white border-gray-200/80 shadow-sm'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-orange-500 bg-orange-500/10">
                <ClipboardList className="w-5 h-5" />
              </div>
              <div>
                <h3 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  Pending Return Queue
                </h3>
                <p className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-gray-500'} font-medium`}>
                  {pendingLoading ? 'Loading...' : `${pendingReturns.length} returns awaiting inspection · Click a return to load it`}
                </p>
              </div>
            </div>

            {pendingLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mr-2" />
                <span className="text-gray-400 text-sm">Loading pending returns...</span>
              </div>
            ) : pendingReturns.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No returns awaiting inspection.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {pendingReturns.map(ret => {
                  const isSelected = selectedReturnId === ret.id;
                  return (
                    <div
                      key={ret.id}
                      onClick={() => setSelectedReturnId(ret.id)}
                      className={`w-full border rounded-2xl p-4 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-500/10 shadow-md'
                          : isDark
                            ? 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                            : 'bg-[#fafaf6] border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-mono font-black text-emerald-500">{ret.displayId}</span>
                        <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 bg-orange-500/15 text-orange-500 rounded">
                          Pending
                        </span>
                      </div>

                      <h4 className={`text-sm font-black mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>{ret.customer}</h4>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[10px] text-gray-500 dark:text-slate-400 font-semibold mb-3">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{ret.orderId}</span>
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Truck className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{ret.courier}</span>
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Archive className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{ret.products.length} product{ret.products.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{ret.date}</span>
                        </div>
                      </div>

                      <div className="px-2.5 py-1.5 bg-rose-500/10 border border-rose-500/20 rounded-lg flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-rose-500 flex-shrink-0" />
                        <span className="text-[9px] font-bold text-rose-500 uppercase tracking-wide truncate">{ret.reason}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── ACTIVE RETURN INLINE SUB-HEADER ── */}
          {!activeReturn ? (
            <div className={`p-3.5 rounded-2xl border flex items-center gap-2.5 text-xs font-semibold transition-colors duration-300 ${
              isDark ? 'bg-slate-900/60 border-slate-800 text-slate-300' : 'bg-[#f4faf7] border-[#e2f3eb] text-[#0f975d]'
            }`}>
              <Info className="w-4 h-4 text-[#0f975d] flex-shrink-0" />
              <span>Select a return from the queue above to begin inspection</span>
            </div>
          ) : (
            <div className={`p-4 rounded-2xl border flex flex-wrap items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-wider transition-colors duration-300 ${
              isDark ? 'bg-slate-900/80 border-slate-800 text-slate-200' : 'bg-gray-50 border-gray-200 text-gray-700'
            }`}>
              <div className="flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-gray-400" /> {activeReturn.orderId}</span>
                <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-gray-400" /> {activeReturn.customer}</span>
                <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-gray-400" /> {activeReturn.courier}</span>
                <span className="flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5 text-gray-400" /> {activeReturn.awb}</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gray-400" /> {activeReturn.date}</span>
              </div>
              <span className="px-2.5 py-0.5 bg-orange-500/15 text-orange-500 rounded text-[9px] font-black uppercase tracking-wide">
                {activeReturn.reason}
              </span>
            </div>
          )}

          {/* ── CARD 2: PRODUCT INSPECTION ── */}
          <div className={`border rounded-2xl p-6 transition-all ${
            isDark ? 'bg-[#1e293b] border-white/10 shadow-2xl' : 'bg-white border-gray-200/80 shadow-sm'
          }`}>
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-orange-500 bg-orange-500/10">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div>
                  <h3 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Product Inspection
                  </h3>
                  <p className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-gray-500'} font-medium`}>
                    Inspect products and make restocking decisions
                  </p>
                </div>
              </div>

              {activeReturn && (
                <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-wider">
                  <span className="text-emerald-500">✓ {totalAcceptedUnits} accepted</span>
                  <span className="text-rose-500">✗ {totalRejectedUnits} rejected</span>
                </div>
              )}
            </div>

            {!selectedReturnId ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className={`w-12 h-12 rounded-xl border flex items-center justify-center text-gray-400 mb-3 ${
                  isDark ? 'bg-slate-900 border-slate-800' : 'bg-gray-50 border-gray-200'
                }`}>
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h4 className={`text-sm font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>No return loaded</h4>
                <p className="text-xs text-gray-400 mt-1 max-w-sm">Choose a pending return above</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[700px]">
                    <thead>
                      <tr className={`border-b text-[10px] font-black uppercase tracking-wider pb-3 ${
                        isDark ? 'border-slate-800 text-slate-400' : 'border-gray-200 text-gray-500'
                      }`}>
                        <th className="pb-3 pl-2">Product · Batch · Expiry</th>
                        <th className="pb-3 text-center w-28">Returned</th>
                        <th className="pb-3 text-center w-28">Accept</th>
                        <th className="pb-3 text-center w-28">Reject</th>
                        <th className="pb-3 text-center w-48">Condition</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-slate-800/60' : 'divide-gray-100'}`}>
                      {activeReturn?.products.map(prod => {
                        const isWarningExpiry = prod.expiryDate && !prod.expiryDate.startsWith('2027');
                        return (
                          <tr key={prod.id} className={`transition-colors ${isDark ? 'hover:bg-slate-800/40' : 'hover:bg-slate-50/50'}`}>
                            <td className="py-4 pl-2">
                              <p className={`text-xs font-bold leading-normal ${isDark ? 'text-white' : 'text-gray-900'}`}>{prod.name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[9px] text-gray-400 font-mono">{prod.sku}</span>
                                <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>
                                  {prod.batchNo}
                                </span>
                                {prod.expiryDate && (
                                  isWarningExpiry ? (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[9px] font-bold">
                                      <AlertTriangle className="w-2.5 h-2.5" />
                                      {prod.expiryDate}
                                    </span>
                                  ) : (
                                    <span className="text-[9px] text-gray-400 font-medium">{prod.expiryDate}</span>
                                  )
                                )}
                              </div>
                            </td>
                            <td className="py-4 text-center">
                              <span className={`inline-flex px-3.5 py-1.5 rounded-lg text-xs font-bold ${isDark ? 'bg-slate-800 text-white border border-slate-700' : 'bg-gray-100 text-gray-700'}`}>
                                {prod.returnedQty}
                              </span>
                            </td>
                            <td className="py-4 text-center">
                              <input
                                type="number"
                                value={prod.acceptQty}
                                onChange={(e) => handleAcceptQtyChange(prod.id, e.target.value)}
                                className={`px-3 py-1.5 rounded-xl border text-sm w-16 text-center outline-none transition-all font-bold ${
                                  isDark ? 'bg-slate-900 border-slate-700 text-emerald-400 focus:border-emerald-500' : 'bg-white border-gray-200 text-emerald-700 focus:border-emerald-500'
                                }`}
                              />
                            </td>
                            <td className="py-4 text-center">
                              <input
                                type="number"
                                value={prod.rejectQty}
                                onChange={(e) => handleRejectQtyChange(prod.id, e.target.value)}
                                className={`px-3 py-1.5 rounded-xl border text-sm w-16 text-center outline-none transition-all font-bold ${
                                  isDark ? 'bg-slate-900 border-slate-700 text-rose-400 focus:border-rose-500' : 'bg-white border-gray-200 text-rose-600 focus:border-rose-500'
                                }`}
                              />
                            </td>
                            <td className="py-4 pr-2">
                              <select
                                value={prod.condition}
                                onChange={(e) => {
                                  setPendingReturns(pendingReturns.map(ret => {
                                    if (ret.id === selectedReturnId) {
                                      return {
                                        ...ret,
                                        products: ret.products.map(p => p.id === prod.id ? { ...p, condition: e.target.value } : p)
                                      };
                                    }
                                    return ret;
                                  }));
                                }}
                                className={`px-3 py-2 rounded-xl border text-xs font-semibold w-full outline-none focus:border-emerald-500 transition-all ${
                                  isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-700'
                                }`}
                              >
                                <option value="Good Resalable">Good Resalable</option>
                                <option value="Damaged/Nonsalable">Damaged/Nonsalable</option>
                                <option value="Needs Re-packaging">Needs Re-packaging</option>
                                <option value="Expired">Expired</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className={`space-y-1.5 border-t pt-4 ${isDark ? 'border-slate-800' : 'border-gray-150'}`}>
                  <label className={`text-[10px] font-black uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    Inspection Remarks (Optional)
                  </label>
                  <textarea
                    rows="2"
                    placeholder="Note any visible damage, quantity discrepancies or handling instructions..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    className={`p-3 rounded-xl border text-xs w-full outline-none focus:border-emerald-500 transition-all resize-none ${
                      isDark ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-gray-200 text-gray-850 placeholder-gray-400'
                    }`}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── CARD 3: RETURN SUMMARY ── */}
          <div className={`border rounded-2xl p-6 transition-all ${
            isDark ? 'bg-[#1e293b] border-white/10 shadow-2xl' : 'bg-white border-gray-200/80 shadow-sm'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-emerald-500 bg-emerald-500/10">
                <FileCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>Return Summary</h3>
                <p className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-gray-500'} font-medium`}>Verification overview of return metrics</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className={`p-4 rounded-xl text-center border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                <p className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-850'}`}>{totalReturnedUnits}</p>
                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-1">Returned</p>
              </div>
              <div className={`p-4 rounded-xl text-center border ${isDark ? 'bg-emerald-950/30 border-emerald-800/40' : 'bg-emerald-50/40 border-emerald-200/50'}`}>
                <p className="text-2xl font-black text-emerald-500">{totalAcceptedUnits}</p>
                <p className="text-[9px] text-emerald-600 font-bold uppercase tracking-wider mt-1">Accepted</p>
              </div>
              <div className={`p-4 rounded-xl text-center border ${isDark ? 'bg-rose-950/30 border-rose-800/40' : 'bg-rose-50/40 border-rose-200/50'}`}>
                <p className="text-2xl font-black text-rose-500">{totalRejectedUnits}</p>
                <p className="text-[9px] text-rose-600 font-bold uppercase tracking-wider mt-1">Rejected</p>
              </div>
            </div>

            <div className="space-y-4 text-xs font-semibold">
              <div className={`flex justify-between border-b pb-2.5 ${isDark ? 'border-slate-800' : 'border-gray-150'}`}>
                <span className="text-gray-400 uppercase tracking-wider">Return ID</span>
                <span className={`font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>{activeReturn?.displayId || '—'}</span>
              </div>
              <div className={`flex justify-between border-b pb-2.5 ${isDark ? 'border-slate-800' : 'border-gray-150'}`}>
                <span className="text-gray-400 uppercase tracking-wider">Order</span>
                <span className={`font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>{activeReturn?.orderId || '—'}</span>
              </div>
              <div className={`flex justify-between border-b pb-2.5 ${isDark ? 'border-slate-800' : 'border-gray-150'}`}>
                <span className="text-gray-400 uppercase tracking-wider">Customer</span>
                <span className={`font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>{activeReturn?.customer || '—'}</span>
              </div>
              <div className={`flex justify-between border-b pb-2.5 ${isDark ? 'border-slate-800' : 'border-gray-150'}`}>
                <span className="text-gray-400 uppercase tracking-wider">Courier</span>
                <span className={`font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>{activeReturn?.courier || '—'}</span>
              </div>
              <div className="flex justify-between pb-1">
                <span className="text-gray-400 uppercase tracking-wider">Reason</span>
                <span className={`font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>{activeReturn?.reason || '—'}</span>
              </div>
            </div>

            <div className={`mt-6 pt-4 border-t space-y-2 ${isDark ? 'border-slate-800' : 'border-gray-150'}`}>
              <label className={`text-[10px] font-black uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Restock to Warehouse
              </label>
              <select
                value={restockWarehouse}
                onChange={(e) => setRestockWarehouse(e.target.value)}
                className={`px-4 py-2.5 rounded-xl border text-sm w-full outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'
                }`}
              >
                {warehousesList.map(wh => (
                  <option key={wh.id} value={wh.id}>{wh.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── CARD 4: ACTIONS ── */}
          <div className={`border rounded-2xl p-6 transition-all ${
            isDark ? 'bg-[#1e293b] border-white/10 shadow-2xl' : 'bg-white border-gray-200/80 shadow-sm'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-emerald-500 bg-emerald-500/10">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>Actions</h3>
                <p className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-gray-500'} font-medium`}>Complete return process and route inventory</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => triggerAction('accept')}
                disabled={submittingAction || !activeReturn}
                className="flex-1 flex items-center justify-center gap-1.5 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-wider text-white shadow-md hover:opacity-90 transition-all cursor-pointer disabled:opacity-50"
                style={{ backgroundColor: currentTheme.color }}
              >
                {submittingAction ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Add Back to Stock
                <ArrowRight className="w-4 h-4 ml-1" />
              </button>

              <button
                onClick={() => triggerAction('reject')}
                disabled={submittingAction || !activeReturn}
                className={`flex-1 flex items-center justify-center gap-1.5 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-wider text-rose-500 border border-rose-500/30 hover:bg-rose-500/10 transition-all cursor-pointer disabled:opacity-50 ${
                  isDark ? 'bg-slate-900' : 'bg-white'
                }`}
              >
                <ThumbsDown className="w-4 h-4" />
                Reject Return
                <ArrowRight className="w-4 h-4 ml-1" />
              </button>

              <button
                onClick={() => triggerAction('hold')}
                disabled={submittingAction || !activeReturn}
                className={`flex-1 flex items-center justify-center gap-1.5 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-wider text-amber-500 border border-amber-500/30 hover:bg-amber-500/10 transition-all cursor-pointer disabled:opacity-50 ${
                  isDark ? 'bg-slate-900' : 'bg-white'
                }`}
              >
                <ShieldAlert className="w-4 h-4" />
                Hold — Pending Decision
              </button>
            </div>

            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-4">
              Accepted items restock into <span className="text-gray-700 dark:text-slate-300">{warehouseName(restockWarehouse) || '—'}</span>. Rejected items are flagged for disposal or vendor return.
            </p>
          </div>
        </div>
      ) : (
        // ── DASHBOARD RETURNS LIST ──
        <>
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="flex items-center gap-6">
              <div>
                <h1 className={`text-2xl desktop:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Returns
                </h1>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'} mt-1`}>
                  {listLoading ? 'Loading...' : `${totalCount} total entries · All warehouses`}
                </p>
              </div>

              <div className="relative w-72 hidden md:block">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </span>
                <input
                  type="text"
                  placeholder="Search by order id, return id or customer name"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className={`pl-10 pr-4 py-2 w-full text-xs font-semibold rounded-full border outline-none transition-all ${
                    isDark
                      ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-400 focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
                  }`}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border transition-all hover:opacity-80 ${
                isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700'
              }`}>
                <FileText className="w-4 h-4 text-rose-500" />
                PDF
              </button>
              <button className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border transition-all hover:opacity-80 ${
                isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700'
              }`}>
                <FileDown className="w-4 h-4 text-emerald-500" />
                Export Excel
              </button>
              <button
                onClick={() => {
                  const firstPending = pendingReturns[0];
                  if (firstPending) setSelectedReturnId(firstPending.id);
                  setShowReceiveForm(true);
                }}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-xs font-black text-white hover:opacity-90 transition-all shadow-md cursor-pointer"
                style={{ backgroundColor: currentTheme.color }}
              >
                <Plus className="w-4 h-4" />
                Receive Return
              </button>
            </div>
          </div>

          {/* ── ERROR BANNER ── */}
          {listError && (
            <div className={`flex items-center gap-3 p-4 rounded-xl border ${
              isDark ? 'bg-rose-950/60 border-rose-800 text-rose-200' : 'bg-rose-50 border-rose-200 text-rose-700'
            } text-sm font-semibold`}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{listError}</span>
              <button onClick={loadReturns} className="ml-auto text-xs font-black uppercase tracking-wider underline cursor-pointer">
                Retry
              </button>
            </div>
          )}

          {/* Metrics row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`flex items-center gap-4 border rounded-2xl p-5 transition-all ${
              isDark ? 'bg-[#1e293b] border-white/10 shadow-2xl' : 'bg-white border-gray-200/80 shadow-sm'
            }`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-emerald-500 bg-emerald-500/10">
                <ClipboardList className="w-6 h-6" />
              </div>
              <div>
                <p className={`text-[10px] font-black uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>PENDING INSPECTIONS</p>
                <p className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'} mt-0.5`}>
                  {pendingLoading ? '—' : String(kpiPendingCount).padStart(2, '0')}
                </p>
              </div>
            </div>

            <div className={`flex items-center gap-4 border rounded-2xl p-5 transition-all ${
              isDark ? 'bg-[#1e293b] border-white/10 shadow-2xl' : 'bg-white border-gray-200/80 shadow-sm'
            }`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-emerald-500 bg-emerald-500/10">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <div>
                <p className={`text-[10px] font-black uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>COMPLETED (IN VIEW)</p>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {listLoading ? '—' : String(kpiCompletedToday).padStart(2, '0')}
                  </span>
                  <span className="text-xs text-gray-400 font-semibold">returns</span>
                </div>
              </div>
            </div>

            <div className={`flex items-center gap-4 border rounded-2xl p-5 transition-all ${
              isDark ? 'bg-[#1e293b] border-white/10 shadow-2xl' : 'bg-white border-gray-200/80 shadow-sm'
            }`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-blue-500 bg-blue-500/10">
                <Archive className="w-6 h-6" />
              </div>
              <div>
                <p className={`text-[10px] font-black uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>ITEMS (IN VIEW)</p>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {listLoading ? '—' : totalItemsProcessed}
                  </span>
                  <span className="text-xs text-gray-400 font-semibold">Units</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Row */}
          <div className={`border rounded-2xl p-4 transition-all ${
            isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200/80'
          }`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <select
                  value={courierFilter}
                  onChange={(e) => setCourierFilter(e.target.value)}
                  className={`appearance-none pl-4 pr-10 py-2 text-xs font-semibold rounded-full border outline-none cursor-pointer transition-all ${
                    isDark ? 'bg-slate-900 border-slate-700 text-white focus:border-emerald-500' : 'bg-white border-gray-200 text-gray-800 focus:border-emerald-500'
                  }`}
                >
                  <option value="All">Courier (All)</option>
                  <option value="Delhivery">Delhivery</option>
                  <option value="Blue Dart">Blue Dart</option>
                  <option value="XpressBees">XpressBees</option>
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                </div>
              </div>

              <div className="relative">
                <select
                  value={warehouseFilter}
                  onChange={(e) => setWarehouseFilter(e.target.value)}
                  className={`appearance-none pl-4 pr-10 py-2 text-xs font-semibold rounded-full border outline-none cursor-pointer transition-all ${
                    isDark ? 'bg-slate-900 border-slate-700 text-white focus:border-emerald-500' : 'bg-white border-gray-200 text-gray-800 focus:border-emerald-500'
                  }`}
                >
                  <option value="All">Warehouse (All)</option>
                  {warehousesList.map(wh => (
                    <option key={wh.id} value={wh.name}>{wh.name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                </div>
              </div>

              <div className="flex items-center">
                <RangePicker
                  value={
                    startDate && endDate
                      ? [dayjs(startDate), dayjs(endDate)]
                      : null
                  }
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setStartDate(dates[0].format('YYYY-MM-DD'));
                      setEndDate(dates[1].format('YYYY-MM-DD'));
                    } else {
                      setStartDate('');
                      setEndDate('');
                    }
                    setCurrentPage(1);
                  }}
                  format="DD-MM-YYYY"
                  style={{ borderRadius: '9999px', padding: '6px 16px' }}
                  className="text-xs font-semibold"
                  size="middle"
                />
              </div>

              {(courierFilter !== 'All' || warehouseFilter !== 'All' || startDate || endDate || searchQuery) && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setCourierFilter('All');
                    setWarehouseFilter('All');
                    setStartDate('');
                    setEndDate('');
                    setCurrentPage(1);
                  }}
                  className="text-xs text-rose-500 hover:text-rose-600 font-black cursor-pointer ml-auto flex items-center gap-1"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className={`border rounded-2xl overflow-hidden transition-all ${
            isDark ? 'bg-[#1e293b] border-white/10 shadow-2xl' : 'bg-white border-gray-200/80 shadow-sm'
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[900px]">
                <thead>
                  <tr className={`border-b text-xs font-black uppercase tracking-wider pb-3 ${
                    isDark ? 'bg-slate-900/60 border-slate-800 text-slate-300' : 'bg-gray-50/80 border-gray-200 text-gray-600'
                  }`}>
                    <th className="py-4 px-6">RETURN ID</th>
                    <th className="py-4 px-6">INITIATED DATE</th>
                    <th className="py-4 px-6">ORDER INFO</th>
                    <th className="py-4 px-6">CUSTOMER NAME</th>
                    <th className="py-4 px-6">ITEMS</th>
                    <th className="py-4 px-6 text-center">STATUS</th>
                    <th className="py-4 px-6">REASON</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-slate-800/60' : 'divide-gray-100'}`}>
                  {listLoading ? (
                    <tr>
                      <td colSpan="7" className="py-12 text-center">
                        <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mx-auto mb-2" />
                        <span className="text-gray-400 text-sm">Loading returns...</span>
                      </td>
                    </tr>
                  ) : filteredReturns.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="py-12 text-center text-xs text-gray-400 font-semibold">
                        No returns found matching the active filters.
                      </td>
                    </tr>
                  ) : (
                    filteredReturns.map((ret) => (
                      <tr
                        key={ret.id}
                        onClick={() => openReturnInForm(ret)}
                        className={`transition-all cursor-pointer ${
                          isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50/80'
                        }`}
                      >
                        <td className="py-4 px-6">
                          <span className={`text-sm font-mono font-bold hover:underline ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                            {ret.displayId}
                          </span>
                        </td>
                        <td className={`py-4 px-6 text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-gray-800'}`}>{ret.date}</td>
                        <td className="py-4 px-6">
                          <p className={`text-sm font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{ret.orderId}</p>
                          <span className={`text-[10px] font-bold tracking-wide uppercase ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            VIA {ret.courier}
                          </span>
                        </td>
                        <td className={`py-4 px-6 text-sm font-black ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>{ret.customer}</td>
                        <td className={`py-4 px-6 text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-gray-800'}`}>
                          {ret.itemsCount} SKU{ret.itemsCount !== 1 ? 's' : ''}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getStatusBadgeClass(ret.status)}`}>
                            {ret.status}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm">
                          <span className={getReasonTextClass(ret.reason)}>{ret.reason}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className={`px-6 py-4 border-t flex items-center justify-between flex-wrap gap-4 text-xs font-semibold ${
              isDark ? 'border-slate-800 text-slate-400' : 'border-gray-200 text-gray-500'
            }`}>
              <div>
                Page {currentPage} of {totalPages} · {totalCount} total results
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  className={`p-1.5 rounded-lg border transition-all ${
                    currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80 cursor-pointer'
                  } ${isDark ? 'border-slate-800 bg-slate-900 text-white' : 'border-gray-200 bg-white text-gray-700'}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }).slice(0, 10).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-8 h-8 rounded-lg border flex items-center justify-center text-xs font-black transition-all cursor-pointer ${
                      currentPage === i + 1
                        ? 'text-white'
                        : isDark ? 'border-slate-800 text-slate-300 hover:bg-slate-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ backgroundColor: currentPage === i + 1 ? currentTheme.color : 'transparent' }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  className={`p-1.5 rounded-lg border transition-all ${
                    currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80 cursor-pointer'
                  } ${isDark ? 'border-slate-800 bg-slate-900 text-white' : 'border-gray-200 bg-white text-gray-700'}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default StockReturns;